import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, InlineNotification, Tray } from '@frontend/design-system';
import { trackingIds } from '../../../../tracking-ids';
import { useCallIntelDemoFlags } from '../../hooks/use-call-intel-demo-flags';
import { CallIntelFiltersContext, useCallIntelFiltersContext } from '../context/call-intel-filters.context';
import { FilterTrayAction } from '../molecules/filter-tray-section/filter-tray-action';
import { FilterTrayContainer } from '../molecules/filter-tray-section/filter-tray-container';
import { FilterValues } from '../types';

interface FilterTrayProps {
  headerLabel?: string;
  containerStyles?: Interpolation<Theme>;
  updateHasModifiedFilters?: (hasModified: boolean) => void;
  onClearAll?: () => void;
  onApplyFilters?: () => void;
  onDismiss?: () => void;
}

export const FilterTray: FC<PropsWithChildren<FilterTrayProps>> = ({
  children,
  headerLabel,
  updateHasModifiedFilters,
  onClearAll,
  onApplyFilters,
  onDismiss,
}) => {
  const { t } = useTranslation('call-intelligence');

  const { isDemoModal } = useCallIntelDemoFlags();

  const callIntelFiltersContext = useCallIntelFiltersContext();
  const { filterValues, filtersModified, errors, isTrayOpen, updateFilterValues, resetFilters, trayControlProps } =
    callIntelFiltersContext;

  const hasModifiedFilters = Object.values(filtersModified).some((isModified) => isModified);
  const hasErrors = useMemo(() => Object.values(errors).some((error) => error), [errors]);

  const officeHoursValidationMessage = typeof errors.officeHours === 'string' ? errors.officeHours : undefined;

  const [trayFilterValues, setTrayFilterValues] = useState<FilterValues>(filterValues);

  const handleClearAll = useCallback(() => {
    resetFilters();
    trayControlProps.closeModal();
    onClearAll?.();
  }, [resetFilters, trayControlProps, onClearAll]);

  const updateTrayFilterValues = useCallback((filters: FilterValues) => {
    setTrayFilterValues((prevValues) => ({ ...prevValues, ...filters }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    updateFilterValues(trayFilterValues);
    trayControlProps.closeModal();
    onApplyFilters?.();
  }, [updateFilterValues, trayControlProps, trayFilterValues, onApplyFilters]);

  const handleDismiss = useCallback(() => {
    trayControlProps.closeModal();
    setTrayFilterValues(filterValues);
    onDismiss?.();
  }, [trayControlProps, filterValues, onDismiss]);

  useEffect(() => {
    updateHasModifiedFilters?.(hasModifiedFilters);
  }, [hasModifiedFilters, updateHasModifiedFilters]);

  useEffect(() => {
    setTrayFilterValues(filterValues);
  }, [filterValues]);

  return (
    <Tray
      {...trayControlProps.modalProps}
      onClose={handleDismiss}
      mountTarget={isDemoModal ? '#data-settings-modal-content' : '#root'}
      width='medium'
      css={styles.tray}
      data-testid='call-intel-filter-tray'
    >
      <Tray.Header
        Buttons={
          <Button
            size='large'
            variant='secondary'
            iconName='x'
            aria-label='Close Filters'
            trackingId={trackingIds.callIntel.filterTrayCloseAction}
            onClick={handleDismiss}
          />
        }
        css={styles.header}
      >
        {headerLabel || t('Filter Call Intelligence')}
      </Tray.Header>
      <Tray.Body css={styles.body}>
        <CallIntelFiltersContext.Provider
          value={{
            ...callIntelFiltersContext,
            filterValues: trayFilterValues,
            updateFilterValues: updateTrayFilterValues,
          }}
        >
          <FilterTrayContainer show={isTrayOpen}>{children}</FilterTrayContainer>
        </CallIntelFiltersContext.Provider>
      </Tray.Body>
      {officeHoursValidationMessage && (
        <InlineNotification message={officeHoursValidationMessage} status='error' size='large' css={styles.banner} />
      )}
      <FilterTrayAction
        primaryAction={{
          label: t('Apply Filters'),
          trackingId: trackingIds.callIntel.filterTrayApplyAction,
          onClick: handleApplyFilters,
          disabled: hasErrors,
        }}
        dismissAction={{
          label: t('Cancel'),
          trackingId: trackingIds.callIntel.filterTrayCloseAction,
          onClick: handleDismiss,
        }}
        clearAllAction={{
          label: t('Clear All Filters'),
          trackingId: trackingIds.callIntel.filterTrayClearAllAction,
          onClick: handleClearAll,
        }}
      />
    </Tray>
  );
};

const styles = {
  tray: css`
    padding: 0;
    @media (max-width: ${breakpoints.xsmall.max}px) {
      width: 100%;
    }
  `,
  header: css`
    padding: ${theme.spacing(2, 4)};
    border-bottom: 1px solid ${theme.colors.neutral10};
  `,
  body: css`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  `,
  banner: css`
    margin: ${theme.spacing(2, 4)};
    > div {
      gap: ${theme.spacing(1)};
      padding: ${theme.spacing(1, 2)};
    }
  `,
};
